import type { Translate } from './profileTypes';

export const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString();
};

export const formatTrialRemaining = (t: Translate, hours?: number | null) => {
  const safeHours = Math.max(0, hours ?? 0);
  if (safeHours < 1) return t('profile.trialLessThanHour');
  if (safeHours < 24) {
    return t('profile.trialHoursRemaining')
      .replace('{{count}}', String(Math.ceil(safeHours)));
  }
  return t('profile.trialDaysRemaining')
    .replace('{{count}}', String(Math.ceil(safeHours / 24)));
};

/**
 * Formats an amount in cents. `fallbackCurrency` is used when `currency` is
 * empty (the pricing endpoint's currency, defaulting to USD).
 */
export const formatCurrencyAmount = (
  t: Translate,
  language: string,
  fallbackCurrency: string,
  amount?: number | null,
  currency?: string | null,
) => {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) {
    return t('profile.priceUnavailable');
  }
  const normalizedCurrency = (currency || fallbackCurrency || 'USD').toUpperCase();
  const value = amount / 100;
  try {
    const locale = language === 'tr' ? 'tr-TR' : undefined;
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: normalizedCurrency,
    }).format(value);
  } catch (error) {
    console.warn('Currency formatting fallback triggered', error);
    return `${normalizedCurrency} ${value.toFixed(2)}`;
  }
};

export const planDisplayName = (t: Translate, planTier?: string) => {
  const mapping: { [key: string]: string } = {
    tier1: t('profile.planIndividual'),
    tier2: t('profile.planAdvanced'),
    tier3: t('profile.planProfessional'),
    individual: t('profile.planIndividual'),
    advanced: t('profile.planAdvanced'),
    professional: t('profile.planProfessional'),
    students: t('profile.planIndividual'),
    student: t('profile.planIndividual'),
    credentials: t('profile.planAdvanced'),
    'chasing credentials': t('profile.planAdvanced'),
    mentor: t('profile.planProfessional'),
    'mentor coach': t('profile.planProfessional'),
  };
  return mapping[planTier?.toLowerCase() || ''] || t('profile.planUnknown');
};

export const profileInitials = (fullName?: string | null, email?: string | null) =>
  (fullName || email || 'MC')
    .split(/[.\s@_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'MC';

export const usagePercent = (used: number, limit: number) =>
  limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
